import { createContext, useContext, type ReactNode } from 'react';
import { useSite } from '../api';
import type { SiteConfig } from '../api/types';
import { DEFAULT_SITE } from './defaults';

/**
 * O valor padrão É a configuração embutida: um componente do chrome montado sem
 * o provider (teste, superfície isolada) renderiza os defaults, nunca quebra.
 */
const SiteConfigContext = createContext<SiteConfig>(DEFAULT_SITE);

/** A forma mínima para o chrome renderizar; uma resposta alheia cai nos defaults. */
function isSiteConfig(value: unknown): value is SiteConfig {
  if (typeof value !== 'object' || value === null) return false;
  const { settings, links, team } = value as Partial<SiteConfig>;
  return (
    typeof settings === 'object' &&
    settings !== null &&
    typeof links === 'object' &&
    links !== null &&
    (Object.keys(DEFAULT_SITE.links) as (keyof SiteConfig['links'])[]).every((area) =>
      Array.isArray(links[area]),
    ) &&
    Array.isArray(team)
  );
}

/**
 * Distribui a configuração do site (`GET /site`) ao chrome. Pendente ou em erro
 * — rollback na Lambda, deploy fora de ordem, rede — valem os defaults: o site
 * nunca cai por causa da configuração. Fica dentro do `BlogApiProvider`.
 */
export function SiteConfigProvider({ children }: { children: ReactNode }): JSX.Element {
  const { data } = useSite();
  const value = isSiteConfig(data) ? data : DEFAULT_SITE;
  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

/** A configuração do site; nunca lança (sem provider, os defaults). */
export function useSiteConfig(): SiteConfig {
  return useContext(SiteConfigContext);
}
